import React, { useCallback, useEffect, useMemo, useState } from "react";

import ClearIcon from "@mui/icons-material/Clear";
import { Box, Button, Drawer, IconButton, InputAdornment, Stack, TextField, Typography } from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { SimpleHeadMui } from "components";
import { SelectAutocomplete } from "components/forms";
import { defaultMeasureDataTableFilters } from "constants/measure-data";
import dayjs from "dayjs";
import { useFormik } from "formik";
import { useActionHook, useDidUpdate } from "hooks";
import { uniqBy } from "lodash";
import {
  useDateFormatSelect,
  useDealActions,
  useDealIdSelector,
  useInvestorsSelector,
  useManagersSelect,
  useMeasureParametersSelector,
  useTaskDealsSelect
} from "store";
import { MeasureDataTableFilters } from "store/types";

interface Props {
  onClose: () => void;
  isOpen: boolean;
  filters: MeasureDataTableFilters;
  setFilters: (data: Partial<MeasureDataTableFilters>) => void;
}

type SetDate = ({
  incomingDate,
  field,
  limit
}: {
  incomingDate: string | null;
  field: keyof Pick<MeasureDataTableFilters, "accountingDate" | "createdAtDate" | "effectiveDate" | "enteredByDate">;
  limit: "from" | "to";
}) => void;

export const FiltersDrawer: React.FC<Props> = ({ isOpen, onClose, filters, setFilters }) => {
  const { getDealRoundsWithInvestorsAction, getUsedMeasuresParametersAction } = useDealActions();

  const { data: roundsWithInvestors = [], action: getDealRoundsWithInvestors } = useActionHook(
    () => dealId && getDealRoundsWithInvestorsAction({ dealId, with_parent_round: true })
  );

  const { data: usedMeasureParameters = [], action: getUsedMeasureParameters } = useActionHook(() =>
    getUsedMeasuresParametersAction(dealId)
  );

  const measures = useMeasureParametersSelector();
  const investors = useInvestorsSelector();
  const managers = useManagersSelect();
  const dateFormat = useDateFormatSelect();
  const deals = useTaskDealsSelect();
  const dealId = useDealIdSelector();

  const isInDealContext = useMemo(() => !!dealId, [dealId]);

  const investorsFromDeal = useMemo(() => {
    if (!roundsWithInvestors) return [];

    return uniqBy(
      roundsWithInvestors.flatMap((round) => round.investors),
      "id"
    );
  }, [roundsWithInvestors]);
  //if we in deal context we show used measures parameters
  const measuresOptions = useMemo(() => {
    if (isInDealContext) {
      return usedMeasureParameters.map(({ id, name }) => ({ id, name }));
    }

    return measures.map(({ id, name }) => ({ id, name }));
  }, [measures, usedMeasureParameters, isInDealContext]);

  const managersOptions = useMemo(() => managers.map(({ id, full_name }) => ({ id, name: full_name })), [managers]);

  const investorsOptions = useMemo(() => {
    //if we in deal context we show investors from deal
    if (isInDealContext) {
      return investorsFromDeal.map(({ id, name }) => ({ id, name }));
    }
    return investors.map(({ id, name }) => ({ id, name }));
  }, [investors, investorsFromDeal, isInDealContext]);

  const [enableReinitialize, setEnableReinitialize] = useState<boolean>(true);

  useEffect(() => {
    if (isInDealContext && isOpen) {
      getDealRoundsWithInvestors();
      getUsedMeasureParameters();
    }
  }, [isInDealContext, isOpen]);

  useDidUpdate(() => {
    // Return back reinitilzation after clear all filters
    if (isOpen && !enableReinitialize) {
      setEnableReinitialize(true);
    }
  }, [isOpen]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { hideDeleted, ...rest } = filters;

  const { values, setFieldValue, handleSubmit, resetForm } = useFormik({
    initialValues: { ...rest },
    onSubmit: (result) => {
      setFilters({ ...result });
      setEnableReinitialize(true);
      onClose();
    },
    enableReinitialize: enableReinitialize
  });

  const setDate: SetDate = useCallback(
    ({ incomingDate, field, limit }) => {
      const currentValue = values[field];

      const date = incomingDate ? new Date(incomingDate.valueOf()) : null;

      if (!date && currentValue?.[limit]) {
        // Delete tate when it comes null
        delete currentValue[limit];
        return setFieldValue(field, currentValue);
      }

      if (currentValue && date) {
        const updatedValue = { ...currentValue, [limit]: new Date(date.valueOf()) };
        return setFieldValue(field, updatedValue);
      }

      if (!currentValue && date) {
        return setFieldValue(field, { [limit]: new Date(date.valueOf()) });
      }
    },
    [values, setFieldValue]
  );

  const isAdjNavSelected = useMemo(() => {
    return values.calculatedFields.includes("Adjusted NAV");
  }, [values.calculatedFields]);

  const isUnfundedSelected = useMemo(() => {
    return values.calculatedFields.includes("Unfunded Commitment");
  }, [values.calculatedFields]);

  const isIrrSelected = useMemo(() => {
    return values.calculatedFields.includes("IRR");
  }, [values.calculatedFields]);

  const onSelectCalcField = useCallback(() => {
    if (values.lastTransactions) {
      setFieldValue("lastTransactions", false);
    }
    setFieldValue("calculatedFields", isAdjNavSelected ? [] : ["Adjusted NAV"]);
  }, [isAdjNavSelected, values.lastTransactions]);

  const onLastTransaction = useCallback(() => {
    if (isAdjNavSelected || isUnfundedSelected || isIrrSelected) {
      setFieldValue("calculatedFields", []);
    }
    setFieldValue("lastTransactions", !values.lastTransactions);
  }, [values.lastTransactions, isAdjNavSelected]);

  const onUnfundedCommitment = useCallback(() => {
    if (values.lastTransactions) {
      setFieldValue("lastTransactions", false);
    }
    setFieldValue("calculatedFields", isUnfundedSelected ? [] : ["Unfunded Commitment"]);
  }, [values.lastTransactions, isUnfundedSelected]);

  const onIrrMeasures = useCallback(() => {
    if (values.lastTransactions) {
      setFieldValue("lastTransactions", false);
    }
    setFieldValue("calculatedFields", isIrrSelected ? [] : ["IRR"]);
  }, [values.lastTransactions, isIrrSelected]);

  const clearForm = useCallback(() => {
    setEnableReinitialize(false);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { hideDeleted, ...rest } = defaultMeasureDataTableFilters;
    resetForm({ values: rest });
  }, []);

  return (
    <Drawer
      sx={{
        form: {
          minWidth: 430,
          maxWidth: 430,
          height: "100%",
          display: "flex",
          flexDirection: "column"
        },
        ".MuiPaper-root": { borderRadius: "0 20px 20px 0" }
      }}
      open={isOpen}
      onClose={onClose}
    >
      <form onSubmit={handleSubmit}>
        <SimpleHeadMui onIconClick={onClose}>Filter and Sort</SimpleHeadMui>
        <Stack
          sx={{
            flexGrow: 1,
            gap: "24px",
            overflowY: "auto",
            p: "18px 30px"
          }}
        >
          <Box
            sx={{
              fontSize: "15px",
              color: "#0a3142",
              pb: "5px",
              lineHeight: "18px",
              borderBottom: "1px solid #dae0e2"
            }}
          >
            Filters
          </Box>
          <Stack direction="row" sx={{ alignItems: "center", gap: "28px" }}>
            <Button
              onClick={onSelectCalcField}
              sx={{ backgroundColor: isAdjNavSelected ? "#dae0e2" : "transparent" }}
              variant="outlined"
            >
              Adjusted NAV
            </Button>
            <Button
              onClick={onLastTransaction}
              sx={{ backgroundColor: values.lastTransactions ? "#dae0e2" : "transparent" }}
              variant="outlined"
            >
              Latest Transactions
            </Button>
          </Stack>
          <Stack direction="row" sx={{ alignItems: "center", gap: "28px" }}>
            <Button
              onClick={onUnfundedCommitment}
              sx={{ backgroundColor: isUnfundedSelected ? "#dae0e2" : "transparent" }}
              variant="outlined"
            >
              Unfunded Commitment
            </Button>
          </Stack>
          <Stack direction="row" sx={{ alignItems: "center", gap: "28px" }}>
            <Button
              onClick={onIrrMeasures}
              sx={{ backgroundColor: isIrrSelected ? "#dae0e2" : "transparent" }}
              variant="outlined"
            >
              IRR Measures
            </Button>
          </Stack>
          {!isInDealContext && (
            <SelectAutocomplete
              options={deals}
              value={values.deals}
              multiple
              selectAll
              renderOption={(props, option) => <li {...props}>{option.title}</li>}
              getOptionLabel={(option) => option.title}
              compareParam={(option) => option.title}
              setValue={(options) => setFieldValue("deals", options)}
              renderInput={(params) => <TextField {...params} label="Deal" />}
              disableCloseOnSelect
            />
          )}

          <SelectAutocomplete
            options={measuresOptions}
            value={values.measures}
            multiple
            selectAll
            renderOption={(props, option) => <li {...props}>{option.name}</li>}
            getOptionLabel={(option) => option.name}
            compareParam={(option) => option.name}
            setValue={(options) => setFieldValue("measures", options)}
            renderInput={(params) => <TextField {...params} label="Parameters" />}
            disableCloseOnSelect
          />
          <SelectAutocomplete
            options={investorsOptions}
            value={values.investors}
            multiple
            selectAll
            renderOption={(props, option) => <li {...props}>{option.name}</li>}
            getOptionLabel={(option) => option.name}
            compareParam={(option) => option.name}
            setValue={(options) => setFieldValue("investors", options)}
            renderInput={(params) => <TextField {...params} label="Investors" />}
            disableCloseOnSelect
          />
          <SelectAutocomplete
            options={managersOptions}
            value={values.enteredBy}
            multiple
            selectAll
            renderOption={(props, option) => <li {...props}>{option.name}</li>}
            getOptionLabel={(option) => option.name}
            compareParam={(option) => option.name}
            setValue={(options) => setFieldValue("enteredBy", options)}
            renderInput={(params) => <TextField {...params} label="Entered By" />}
            disableCloseOnSelect
          />
          <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center", gap: "16px" }}>
            <DesktopDatePicker
              format={dateFormat}
              views={["year", "month", "day"]}
              value={values.effectiveDate?.from ? dayjs(values.effectiveDate.from) : null}
              label="Effective Date from"
              onChange={(date) => {
                if (date && date.isValid()) {
                  setDate({ incomingDate: date && date?.toISOString(), field: "effectiveDate", limit: "from" });
                }
              }}
              slotProps={{
                inputAdornment: { position: "start" },
                textField: {
                  InputProps: {
                    endAdornment: values.effectiveDate?.from ? (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setDate({ incomingDate: null, field: "effectiveDate", limit: "from" })}
                          edge="end"
                        >
                          <ClearIcon />
                        </IconButton>
                      </InputAdornment>
                    ) : null
                  }
                },
                popper: {
                  sx: {
                    ".MuiPickersCalendarHeader-labelContainer": {
                      fontSize: "0.9rem"
                    }
                  }
                }
              }}
            />
            -
            <DesktopDatePicker
              format={dateFormat}
              views={["year", "month", "day"]}
              value={values.effectiveDate?.to ? dayjs(values.effectiveDate.to) : null}
              label="to"
              onChange={(date) => {
                if (date && date.isValid()) {
                  setDate({ incomingDate: date && date.toISOString(), field: "effectiveDate", limit: "to" });
                }
              }}
              slotProps={{
                inputAdornment: { position: "start" },
                textField: {
                  InputProps: {
                    endAdornment: values.effectiveDate?.to ? (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setDate({ incomingDate: null, field: "effectiveDate", limit: "to" })}
                          edge="end"
                        >
                          <ClearIcon />
                        </IconButton>
                      </InputAdornment>
                    ) : null
                  }
                },
                popper: {
                  sx: {
                    ".MuiPickersCalendarHeader-labelContainer": {
                      fontSize: "0.9rem"
                    }
                  }
                }
              }}
            />
          </Stack>
          <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center", gap: "16px" }}>
            <DesktopDatePicker
              format={dateFormat}
              views={["year", "month", "day"]}
              value={values.accountingDate?.from ? dayjs(values.accountingDate.from) : null}
              label="Accounting Date from"
              onChange={(date) => {
                if (date && date.isValid()) {
                  setDate({ incomingDate: date && date.toISOString(), field: "accountingDate", limit: "from" });
                }
              }}
              slotProps={{
                inputAdornment: { position: "start" },
                textField: {
                  InputProps: {
                    endAdornment: values.accountingDate?.from ? (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setDate({ incomingDate: null, field: "accountingDate", limit: "from" })}
                          edge="end"
                        >
                          <ClearIcon />
                        </IconButton>
                      </InputAdornment>
                    ) : null
                  }
                },
                popper: {
                  sx: {
                    ".MuiPickersCalendarHeader-labelContainer": {
                      fontSize: "0.9rem"
                    }
                  }
                }
              }}
            />
            -
            <DesktopDatePicker
              format={dateFormat}
              views={["year", "month", "day"]}
              value={values.accountingDate?.to ? dayjs(values.accountingDate.to) : null}
              label="to"
              onChange={(date) => {
                if (date && date.isValid()) {
                  setDate({ incomingDate: date && date.toISOString(), field: "accountingDate", limit: "to" });
                }
              }}
              slotProps={{
                inputAdornment: { position: "start" },
                textField: {
                  InputProps: {
                    endAdornment: values.accountingDate?.to ? (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setDate({ incomingDate: null, field: "accountingDate", limit: "to" })}
                          edge="end"
                        >
                          <ClearIcon />
                        </IconButton>
                      </InputAdornment>
                    ) : null
                  }
                },
                popper: {
                  sx: {
                    ".MuiPickersCalendarHeader-labelContainer": {
                      fontSize: "0.9rem"
                    }
                  }
                }
              }}
            />
          </Stack>
          <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center", gap: "16px" }}>
            <DesktopDatePicker
              format={dateFormat}
              views={["year", "month", "day"]}
              value={values.createdAtDate?.from ? dayjs(values.createdAtDate.from) : null}
              label="Created At Date from"
              onChange={(date) => {
                if (date && date.isValid()) {
                  setDate({ incomingDate: date && date.toISOString(), field: "createdAtDate", limit: "from" });
                }
              }}
              slotProps={{
                inputAdornment: { position: "start" },
                textField: {
                  InputProps: {
                    endAdornment: values.createdAtDate?.from ? (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setDate({ incomingDate: null, field: "createdAtDate", limit: "from" })}
                          edge="end"
                        >
                          <ClearIcon />
                        </IconButton>
                      </InputAdornment>
                    ) : null
                  }
                },
                popper: {
                  sx: {
                    ".MuiPickersCalendarHeader-labelContainer": {
                      fontSize: "0.9rem"
                    }
                  }
                }
              }}
            />
            -
            <DesktopDatePicker
              format={dateFormat}
              views={["year", "month", "day"]}
              value={values.createdAtDate?.to ? dayjs(values.createdAtDate.to) : null}
              label="to"
              onChange={(date) => {
                if (date && date.isValid()) {
                  setDate({ incomingDate: date && date.toISOString(), field: "createdAtDate", limit: "to" });
                }
              }}
              slotProps={{
                inputAdornment: { position: "start" },
                textField: {
                  InputProps: {
                    endAdornment: values.createdAtDate?.to ? (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setDate({ incomingDate: null, field: "createdAtDate", limit: "to" })}
                          edge="end"
                        >
                          <ClearIcon />
                        </IconButton>
                      </InputAdornment>
                    ) : null
                  }
                },
                popper: {
                  sx: {
                    ".MuiPickersCalendarHeader-labelContainer": {
                      fontSize: "0.9rem"
                    }
                  }
                }
              }}
            />
          </Stack>

          <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center", gap: "16px" }}>
            <DesktopDatePicker
              format={dateFormat}
              views={["year", "month", "day"]}
              value={values.enteredByDate?.from ? dayjs(values.enteredByDate.from) : null}
              label="Entered By Date from"
              onChange={(date) => {
                if (date && date.isValid()) {
                  setDate({ incomingDate: date && date.toISOString(), field: "enteredByDate", limit: "from" });
                }
              }}
              slotProps={{
                inputAdornment: { position: "start" },
                textField: {
                  InputProps: {
                    endAdornment: values.enteredByDate?.from ? (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setDate({ incomingDate: null, field: "enteredByDate", limit: "from" })}
                          edge="end"
                        >
                          <ClearIcon />
                        </IconButton>
                      </InputAdornment>
                    ) : null
                  }
                },
                popper: {
                  sx: {
                    ".MuiPickersCalendarHeader-labelContainer": {
                      fontSize: "0.9rem"
                    }
                  }
                }
              }}
            />
            -
            <DesktopDatePicker
              format={dateFormat}
              views={["year", "month", "day"]}
              value={values.enteredByDate?.to ? dayjs(values.enteredByDate.to) : null}
              label="to"
              onChange={(date) => {
                if (date && date.isValid()) {
                  setDate({ incomingDate: date && date.toISOString(), field: "enteredByDate", limit: "to" });
                }
              }}
              slotProps={{
                inputAdornment: { position: "start" },
                textField: {
                  InputProps: {
                    endAdornment: values.enteredByDate?.to ? (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setDate({ incomingDate: null, field: "enteredByDate", limit: "to" })}
                          edge="end"
                        >
                          <ClearIcon />
                        </IconButton>
                      </InputAdornment>
                    ) : null
                  }
                },
                popper: {
                  sx: {
                    ".MuiPickersCalendarHeader-labelContainer": {
                      fontSize: "0.9rem"
                    }
                  }
                }
              }}
            />
          </Stack>
        </Stack>
        <Stack
          direction="row"
          sx={{
            alignItems: "center",
            justifyContent: "space-between",
            boxShadow: "0 -1px 5px 1px rgba(0,0,0,.08)",
            p: "18px 30px"
          }}
        >
          <Typography
            sx={{
              fontSize: "16px",
              textDecoration: "underline",
              fontWeight: 500,
              cursor: "pointer"
            }}
            onClick={clearForm}
          >
            Clear All Filters
          </Typography>
          <Stack direction="row" gap="18px">
            <Button variant="outlined" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" variant="contained">
              Apply
            </Button>
          </Stack>
        </Stack>
      </form>
    </Drawer>
  );
};
